import { apiGet, apiPost } from "../api";

interface AppleTokenResponse {
    token: string;
    expiresAt: number;
}

let configured = false;
let configuring: Promise<void> | null = null;

async function configure(): Promise<void> {
    const { token } = await apiGet<AppleTokenResponse>('/v1/apple/token');
    await window.MusicKit.configure({
        developerToken: token,
        app: { name: 'Shufl', build: '1.0.0' },
    });
    configured = true;
}

// MusicKit.configure() only needs to run once, but connect()/isLoggedIn() can both
// race to call it on first use - share one in-flight promise instead of double-fetching
// the developer token or configuring twice. Track this ourselves rather than probing
// MusicKit.getInstance(), which throws (rather than returning falsy) when nothing has
// been configured yet.
export async function ensureConfigured(): Promise<void> {
    if (configured) {
        return;
    }
    if (!configuring) {
        configuring = configure().catch(err => {
            configuring = null;
            throw err;
        });
    }
    return configuring;
}

export function getMusicKitInstance() {
    if (!window.MusicKit) {
        throw new Error('MusicKit failed to load.');
    }
    return window.MusicKit.getInstance();
}

/**
 * True Apple Music login check. Settings' "Connected" badge reflects the backend
 * having a stored Music-User-Token - a completely separate fact from whether *this*
 * browser tab's MusicKit instance has been configured yet. Without ensureConfigured()
 * first, MusicKit.getInstance() throws "No configured instance" on a fresh page load
 * (nothing has called configure() yet), isAuthorized is never reached, and this always
 * reports false even for a genuinely connected account - which is exactly what made
 * linking say "not connected" right after Settings said "Connected".
 */
export async function isLoggedIn(): Promise<boolean> {
    try {
        await ensureConfigured();
        return !!getMusicKitInstance()?.isAuthorized;
    } catch {
        return false;
    }
}

/**
 * Opens Apple's own sign-in flow via MusicKit. The user completes that
 * themselves in Apple's UI - this never sees their Apple ID or password.
 * Stores the resulting Music-User-Token on the backend once they do.
 */
export async function connect(): Promise<void> {
    await ensureConfigured();
    const musicUserToken: string = await getMusicKitInstance().authorize();
    if (!musicUserToken) {
        throw new Error('Apple Music authorization was cancelled.');
    }
    await apiPost('/v1/apple/userToken', { musicUserToken });
}

export async function disconnect(): Promise<void> {
    await ensureConfigured();
    await getMusicKitInstance().unauthorize();
}
